import React, { FunctionComponent as FC, useState, useEffect, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux'; // To call Action Creators
import { bindActionCreators } from 'redux';
import { State } from '../../../../../state';
import { actionCreators } from '../../../../../state';
import './index.scss';
import * as d3 from 'd3';
import * as _ from 'lodash';

import { MainViewTypes } from '../../../../../types/data';
// import useEntityTypeLayoutCalculation from '../useEntityTypeLayoutCalculation';

// ============= helper functions =============
// Seeded random number generator for consistent layout results
const createSeededRandom = (seed: number) => {
  let state = seed;
  return () => {
      // Linear congruential generator (LCG)
      state = (state * 1664525 + 1013904223) % Math.pow(2, 32);
      return state / Math.pow(2, 32);
  };
};


interface EntityTypeLevelGraph2025Props {
  entityTypeGraphData: MainViewTypes.EntityTypeGraphData;
  selectedImpactTypeList: string[];
  impactType: string;
}

const EntityTypeLevelGraph2025: FC<EntityTypeLevelGraph2025Props> = (props) => {

  const { entityTypeGraphData, selectedImpactTypeList, impactType } = props

  const dispatch = useDispatch()
  const {
    updateIsEntityTypeGraphFinished,
    updateEntityTypeLayoutData,

    setShowEntityTypeLevelGraph,
    setShowEntityFieldLevelGraph,
  } = bindActionCreators(actionCreators, dispatch)

  console.log('================EntityTypeLevelGraph2025: started! =================')

  // data reducer
  const entityFieldGraph_InputData: any[] = useSelector((state: State) => state.dataReducer?.entityFieldGraph_InputData) as any[]


  // ui reducer
  const svgSize: MainViewTypes.SVGSize = useSelector((state: State) => state.uiReducer?.svgSize) as MainViewTypes.SVGSize


  // ============================================
  // ============= helper functions =============
  // ============================================
  // --- force-directed layout (start) ---
  // opt 1: without useEffect
  const calculateForceDirectedLayout = (inputData: {
    nodes: MainViewTypes.EntityTypeNode[];
    links: MainViewTypes.EntityTypeLink[];
  }, seed: number = 42) => {
    // Using seed=42 for consistent results. Change the seed to get different layouts:
    // - Same seed = same layout every time
    // - Different seeds = different but consistent layouts
    // - You can also use a hash of your data as seed for data-dependent layouts

    if (!inputData || !inputData.nodes || inputData.nodes.length === 0) {
        return {
            nodes: [],
            nodeDirections: {},
            nodeDistances: {},
            nodeDirectionsDegrees: {},
            normalizedDistances: {}
        };
    }

    // Create seeded random function
    const seededRandom = createSeededRandom(seed);

    // Initialize nodes with seeded random positions
    const initializedNodes = inputData.nodes.map(node => ({
        ...node,
        x: seededRandom() * 800,
        y: seededRandom() * 800,
    }));

    // Find and fix grant node position at center
    const grantNode = initializedNodes.find(node => node.id === 'grant');
    if (grantNode) {
        grantNode.fx = svgSize.width / 2;
        grantNode.fy = svgSize.height / 2;
    }

    // Initialize links
    const initializedLinks = inputData.links.map(link => ({
        ...link,
        source: initializedNodes.find(n => n.id === link.source.id) || initializedNodes[0],
        target: initializedNodes.find(n => n.id === link.target.id) || initializedNodes[0],
    }));

    // Calculate max values for normalization
    const maxLinkValue = inputData.links.length
        ? Math.max(...inputData.links.map(l => l.value))
        : 1;

    // Create simulation
    const simulation = d3.forceSimulation(initializedNodes)
        .force("link", d3.forceLink(initializedLinks)
            .id((d: any) => (d as MainViewTypes.EntityTypeNode).id)
            .distance(link => {
                if (link.source.id === 'grant') {
                    return 300; // Base distance from grant node
                } else {
                    return 320;
                }
            })
            .strength(link => {
              // return 1
              // Normalize link strength based on value
              // return 1 - Math.pow((link.value / maxLinkValue), 0.95);

              // return 1 - Math.pow((link.value / maxLinkValue), 0.3);
              if(inputData.nodes.length >= 3) {
                return 1 - Math.pow((link.value / maxLinkValue), 0.3);
              } else {
                return 0.3 // deal with: when users only select one impact type.
              }
            })
        )
        .force("charge", d3.forceManyBody().strength(node => {
            // Adjust repulsion based on num_doc
            const baseStrength = -22.5;
            const scalingFactor = 0.17;
            // @ts-ignore
            return baseStrength - (node.num_doc * scalingFactor);
        }))
        .force("center", d3.forceCenter(svgSize.width / 2, svgSize.height / 2))
        .alphaDecay(0.1)
        .force("collide", d3.forceCollide().radius(18)); // deal with: when users only select one impact type.

    // Run simulation to completion without animation
    // Run for enough ticks to ensure convergence
    for (let i = 0; i < 300; ++i) {
        simulation.tick();
    }

    // Stop the simulation
    simulation.stop();

    // Post-simulation: Calculate directions and distances from grant node
    const nodeDirections: { [nodeId: string]: number } = {};
    const nodeDirections_ray_unit_vectors: { [nodeId: string]: number[] } = {};
    const nodeDistances: { [nodeId: string]: number } = {};
    const nodeDirectionsDegrees: { [nodeId: string]: number } = {};
    const normalizedDistances: { [nodeId: string]: number } = {};

    const grantNodeFinal = simulation.nodes().find(n => n.id === 'grant');

    if (grantNodeFinal) {
        const distances: number[] = [];

        // First pass: calculate all distances to find max for normalization
        simulation.nodes().forEach(node => {
            if (node.id !== 'grant' && node.x !== undefined && node.y !== undefined) {
                const dx = node.x - grantNodeFinal.x!;
                const dy = node.y - grantNodeFinal.y!;
                const distance = Math.sqrt(dx * dx + dy * dy);
                distances.push(distance);
            }
        });

        const maxDistance = Math.max(...distances);
        const minDistance = Math.min(...distances);

        // Second pass: calculate directions, distances, and normalized values
        simulation.nodes().forEach(node => {
            if (node.id !== 'grant' && node.x !== undefined && node.y !== undefined) {
                // Calculate direction (angle in radians)
                const dx = node.x - grantNodeFinal.x!;
                const dy = node.y - grantNodeFinal.y!;
                const angle = Math.atan2(dy, dx);
                nodeDirections[node.id] = angle;

                // Calculate ray unit vector
                nodeDirections_ray_unit_vectors[node.id] = [Math.cos(angle), Math.sin(angle)];

                // Convert to degrees
                nodeDirectionsDegrees[node.id] = (angle * 180) / Math.PI;

                // Calculate distance
                const distance = Math.sqrt(dx * dx + dy * dy);
                nodeDistances[node.id] = distance;

                // Normalize distance to 0-1 range
                normalizedDistances[node.id] = maxDistance > minDistance
                    ? (distance - minDistance) / (maxDistance - minDistance)
                    : 0;
            }
        });
    }

    return {
        nodes: simulation.nodes(),
        nodeDirections,        // angles in radians
        nodeDirections_ray_unit_vectors, // ray unit vectors
        nodeDistances,         // absolute distances
        nodeDirectionsDegrees, // angles in degrees
        normalizedDistances    // distances normalized to 0-1 range
    };
  };
  // --- force-directed layout (end) ---

  const useEntityTypeLayoutCalculation = (
    entityTypeGraphData: MainViewTypes.EntityTypeGraphData,
    selectedImpactTypeList: string[],
    impactType: string
  ) => {

    const [entityTypeLayoutData, setEntityTypeLayoutData] = useState<any>(null);
    const [updatedEntityFieldData, setUpdatedEntityFieldData] = useState<any>(null);

    useEffect(() => {
      // 2. filter entityTypeGraphData, based on selected `impactType` and `selectedImpactTypeList`
      let entityTypeGraph_InputData: {
        nodes: MainViewTypes.EntityTypeNode[];
        links: MainViewTypes.EntityTypeLink[];
      } = entityTypeGraphData[impactType]
      const entityTypeList = _.cloneDeep(selectedImpactTypeList)
      entityTypeList.push('grant')
      let nodesFiltered = entityTypeGraph_InputData?.nodes.filter((node: MainViewTypes.EntityTypeNode) => entityTypeList.includes(node.id))
      let linksFiltered = entityTypeGraph_InputData?.links.filter((link: MainViewTypes.EntityTypeLink) => entityTypeList.includes(link.source.id) && entityTypeList.includes(link.target.id))
      entityTypeGraph_InputData = {
          nodes: nodesFiltered,
          links: linksFiltered,
      }
      // console.log('entityTypeList: ', entityTypeList)
      // console.log('entityTypeGraph_InputData: ', entityTypeGraph_InputData)

      // 3. d3-force-directed layout
      // opt 2: no animation, just update the state with the final node and link positions
      // Calculate layout and get final positions and directions
      const layoutResult = calculateForceDirectedLayout(entityTypeGraph_InputData, 42);
      // You can now use layoutResult.nodeDirections and layoutResult.nodeDistances
      // nodeDirections contains angles in radians for each node from grant node
      // nodeDistances contains relative distances for each node from grant node

      // console.log('layoutResult: ', layoutResult)
      const updatedEntityFieldData = layoutResult.nodes
      // const updatedEntityFieldData = entityFieldGraph_InputData.map(entity => {
      //   const node = nodeCurrent.find(n => n.id === entity.type) // Find the corresponding node from the force simulation

      //   if (node?.id === 'patent') {
      //     return {
      //       ...entity,
      //       x: node?.x + dx,
      //       y: node?.y + dy,
      //       // x: node?.x ?? entity.x, // Update position, fallback to original if not found
      //       // y: node?.y ?? entity.y,
      //     };
      //   } else {
      //     return {
      //       ...entity,
      //       x: node?.x,
      //       y: node?.y,
      //       // x: node?.x ?? entity.x, // Update position, fallback to original if not found
      //       // y: node?.y ?? entity.y,
      //     };
      //   }
      // });


      // // 1. 过滤数据
      // const entityTypeList = [...selectedImpactTypeList, 'grant'];
      // const filteredData = filterEntityTypeData(entityTypeGraphData[impactType], entityTypeList);

      // // 2. 计算 force-directed layout
      // const rawLayoutResult = calculateForceDirectedLayout(filteredData, 42);

      // // 3. 提取边的方向和相对长度（允许等比例scale）
      // const result: EntityTypeLayoutResult = {
      //   nodeDirections: rawLayoutResult.nodeDirections,
      //   nodeDistances: rawLayoutResult.nodeDistances,
      //   edgeDirections: extractEdgeDirectionsAndLengths(rawLayoutResult)
      // };

      setEntityTypeLayoutData(layoutResult);
      setUpdatedEntityFieldData(updatedEntityFieldData);
    }, [entityTypeGraphData, selectedImpactTypeList, impactType]);

    return {entityTypeLayoutData, updatedEntityFieldData};
  };














  // reset flag when inputs change
  useEffect(() => {
    updateIsEntityTypeGraphFinished(false)
  }, [entityTypeGraphData, selectedImpactTypeList, impactType, updateIsEntityTypeGraphFinished])

  const { entityTypeLayoutData, updatedEntityFieldData } = useEntityTypeLayoutCalculation(entityTypeGraphData, selectedImpactTypeList, impactType);

  // console.log('==> EntityTypeLevelGraph2025: updatedEntityFieldData: ', updatedEntityFieldData)

  useEffect(() => {
    if (entityTypeLayoutData) {
      // data reducer
      updateEntityTypeLayoutData(entityTypeLayoutData)
      // updateEntityFieldGraph_InputData(updatedEntityFieldData)

      // ui reducer
      updateIsEntityTypeGraphFinished(true)
      setShowEntityTypeLevelGraph(true)
      setShowEntityFieldLevelGraph(true)
    }
  }, [entityTypeLayoutData, updateEntityTypeLayoutData, updateIsEntityTypeGraphFinished])

  // debug (result: correct)
  let entityTypeLevelGraph: JSX.Element = <></>
  if (entityTypeLayoutData) {
    // console.log('entityTypeLayoutData: ', entityTypeLayoutData)
    // console.log('Final node positions:', entityTypeLayoutData.nodes);
    // console.log('Node directions from grant:', entityTypeLayoutData.nodeDirections);
    // console.log('Node directions_ray_unit_vectors from grant:', entityTypeLayoutData.nodeDirections_ray_unit_vectors);
    // console.log('Node distances from grant:', entityTypeLayoutData.nodeDistances);
    // Draw element (for debug)
    entityTypeLevelGraph = <g id="entity-type-level-graph-2025" opacity={0}>
        {entityTypeLayoutData.nodes.map((node: MainViewTypes.EntityTypeNode) => (
            <g key={node.id} id={`entity-type-node-${node.id}`}>
                <circle cx={node.x} cy={node.y} r={10} />
                <text x={node.x} y={node.y} textAnchor="middle" dominantBaseline="middle">
                    {node.id}
                </text>
            </g>
        ))}
        {
          // generate links from `grant` to other nodes
          entityTypeLayoutData.nodes
            .filter((node: MainViewTypes.EntityTypeNode) => node.id !== 'grant')
            .map((node: MainViewTypes.EntityTypeNode) => {
              const grantNode = entityTypeLayoutData.nodes.find((n: MainViewTypes.EntityTypeNode) => n.id === 'grant');
              return (
                <g key={`link-grant-to-${node.id}`}>
                  <line
                    x1={grantNode?.x}
                    y1={grantNode?.y}
                    x2={node.x}
                    y2={node.y}
                    stroke="#999"
                    strokeWidth={1}
                    strokeOpacity={0.6}
                  />
                </g>
              );
            })
        }
    </g>
  }

  let entityFieldLevelGraphAfterOpotimization: JSX.Element = <></>
  if(entityFieldGraph_InputData) {
    entityFieldLevelGraphAfterOpotimization = <g id="entity-field-level-graph-after-optimization">
      {entityFieldGraph_InputData.map((entity: any, index: number) => (
        <g key={index} id={`entity-field-node-${entity.id}`}>
          <circle cx={entity.x} cy={entity.y} r={10} fill="steelblue" />
        </g>
      ))}
    </g>
  }


  console.log('================EntityTypeLevelGraph2025: ended! =================')

  return (
    <>
      {entityTypeLevelGraph}
      {/* {entityFieldLevelGraphAfterOpotimization} */}
    </>
  )
}
export default EntityTypeLevelGraph2025;



